import React from 'react';
import Cropper from 'react-cropper';
import { Button, Tooltip, OverlayTrigger, Form } from 'react-bootstrap';
import Slider from 'rc-slider/lib/Slider';
import { removeBackgroundFromImage } from './BackgroundRemoval';
import { LoadingSpinner } from '../components/LoadingSpinner';
import extendImage from './imageExtention';
import { extendImageGenerative, validateImageForExtension, generateDataUrlFromImage } from './generativeImageService';

function ImageEditor({
  cropperRef,
  editorImageSrc,
  scaleFree,
  freeScale,
  xZoom,
  possibleZooms,
  imgBlockWidth,
  imgBlockHeight,
  imgWidth,
  imgHeight,
  imageUpload,
  rotateCropperImage,
  mirrorCropperImage,
  zoomCropperImage,
  setCropperScaleFree,
  resetCropperImage,
  messages,
  disabled,
  changeImage = null,
  handleAiGeneratedImage = null,
  aiGenerated = false
}) {

  // Background removal settings
  const [isRemovalInProgress, setIsRemovalInProgress] = React.useState(false);
  const [isExtensionInProgress, setIsExtensionInProgress] = React.useState(false);
  const [isGenerativeExtensionPossible, setIsGenerativeExtensionPossible] = React.useState(true);
  const [markAsAiGenerated, setMarkAsAiGenerated] = React.useState(aiGenerated);

  React.useEffect(() => {
    // console.log('ImageEditor received new editorImageSrc:', editorImageSrc);
    if (editorImageSrc) {
      if (!validateImageForExtension(editorImageSrc)) {
        setIsGenerativeExtensionPossible(false);
      } else {
        setIsGenerativeExtensionPossible(true);
      }
    }
  }, [editorImageSrc]);

  // default settings - imgSizes > minSizes
  let ratio = imgBlockHeight / imgBlockWidth;
  let autoCrop = imgBlockWidth / imgWidth;
  let customViewMode = 2;
  let allowZoom = true;

  // minWidth > imgWidth (imgBlockWidth)
  if (imgBlockWidth > imgWidth) {
    allowZoom = false;
    autoCrop = 1;
    customViewMode = 0;
    ratio = imgBlockHeight / imgWidth;

  }

  // minHeight > imgHeight (imgBlockHeigh)
  if (imgBlockHeight > imgHeight) {
    allowZoom = false;
    autoCrop = 1;
    customViewMode = 0;
    ratio = imgHeight / imgBlockWidth;
  }

  // imgSizes < minSizes
  if (imgBlockWidth > imgWidth && imgBlockHeight > imgHeight) {
    allowZoom = false;
    autoCrop = 1;
    customViewMode = 0;
    ratio = imgHeight / imgWidth;
  }

  let customRatio = 1 / ratio;


  // Background removal button handler
  const handleBackgroundRemoval = async () => {
    if (isRemovalInProgress) {
      console.log('Background removal is already in progress.');
      return;
    }

    try {
      setIsRemovalInProgress(true);
      const result = await removeBackgroundFromImage(editorImageSrc.src);
      console.log('Background removed image data:', result);
      const newImageDataUrl = `data:image/png;base64,${result.imageData}`;

      const newImg = new Image();
      newImg.src = newImageDataUrl;
      newImg.width = editorImageSrc.width;
      newImg.height = editorImageSrc.height;
      newImg.crossOrigin = 'Anonymous';
      newImg.onload = () => {
        console.log('New image loaded with dimensions:', newImg.width, newImg.height);
        cropperRef.current.cropper.replace(newImageDataUrl);
        if (changeImage) {
          console.log('Calling changeImage with new image dimensions:', newImg.width, newImg.height);
          changeImage(newImg);
        }
        setIsRemovalInProgress(false);
      };

    } catch (error) {
      console.error('Error removing background:', error);
      setIsRemovalInProgress(false);
    }
  }

  const handleImageExtension = async () => {
    try {
      const extendedImage = extendImage(editorImageSrc);
      extendedImage.onload = () => {
        cropperRef.current.cropper.replace(extendedImage.src);
        if (changeImage) {
          console.log('Calling changeImage with extended image dimensions:', extendedImage.width, extendedImage.height);
          changeImage(extendedImage);
        }
      };
    } catch (error) {
      console.error('Error extending image:', error);
    }
  }

  const handleGenerativeExtension = async () => {
    try {
      // Validate the image before attempting generative extension
      setIsExtensionInProgress(true);
      if (!validateImageForExtension(editorImageSrc)) {
        console.error('Image validation failed for generative extension.');
        setIsExtensionInProgress(false);
        return;
      }
      // Create a data URL from the image and send it to the generative image service for extension
      await generateDataUrlFromImage(editorImageSrc).then(async (dataUrl) => {
        // Extend the image using the generative image service and replace the cropper image with the extended version
        await extendImageGenerative(dataUrl).then((extendedImageDataUrl) => {
          // Replace the image with the extended version and call changeImage with the new dimensions
          const extendedImage = new Image();
          extendedImage.src = extendedImageDataUrl;
          extendedImage.onload = () => {
            cropperRef.current.cropper.replace(extendedImage.src);
            if (changeImage) {
              console.log('Calling changeImage with generatively extended image dimensions:', extendedImage.width, extendedImage.height);
              changeImage(extendedImage);
            }
            setIsExtensionInProgress(false);
          };
        }).catch((error) => {
          console.error('Error extending image generatively:', error);
          setIsExtensionInProgress(false);
        });
      }).catch((error) => {
        console.error('Error generating data URL from image:', error);
        setIsExtensionInProgress(false);
      });
    } catch (error) {
      console.error('Error extending image generatively:', error);
      setIsExtensionInProgress(false);
    }
  }

  const tooltip = (message) => (
    <Tooltip id="button-tooltip">
      {message}
    </Tooltip>
  );

  return (

    <>
      <Cropper
        ref={cropperRef}
        style={{ height: '80%', width: '100%' }}
        src={editorImageSrc.src}
        viewMode={customViewMode} // default 2
        className={`image-edit${isRemovalInProgress ? " locked" : ""}`}
        crossOrigin="true" // boolean, set it to true if your image is cors protected or it is hosted on cloud like aws s3 image server
        dragMode="move"
        scalable
        movable
        zoomable
        rotatable
        aspectRatio={customRatio}
        zoomOnTouch={false}
        zoomOnWheel={false}
        toggleDragModeOnDblclick={false}
        autoCropArea={autoCrop}

      //zoomTo={0.5} - new
      //initialAspectRatio={1} - new
      //minCropBoxHeight={10} - new
      //minCropBoxWidth={10} - new
      //background={false} - new
      //responsive{true} - new
      //autoCropArea={1} - new
      //checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671 - new
      //guides - new

      />
      <div style={{ padding: '10px' }} className='d-flex align-items-center justify-content-center'>
        {isRemovalInProgress && <LoadingSpinner message={messages.editor.background_removal_in_progress} size={16} />}
      </div>
      <div style={{ padding: '10px' }}>
        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.reset_image || "Reset Image")}>
          <Button variant="light" size="large" style={{ zIndex: 101 }} onClick={() => resetCropperImage()} className="" disabled={disabled}>
            <i className="fad fa-eraser" />
          </Button>
        </OverlayTrigger>

        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.remove_background || "Remove Background")}>
          <Button
            variant="light"
            size="large"
            style={{ zIndex: 101 }}
            onClick={() => handleBackgroundRemoval()}
            className=""
            disabled={disabled || isRemovalInProgress}
          >
            <i class="fa-solid fa-wand-magic-sparkles"></i>
          </Button>
        </OverlayTrigger>

        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.extend_image || "Extend Image")}>
          <Button
            variant="light"
            size="large"
            style={{ zIndex: 101 }}
            onClick={() => handleImageExtension()}
            className=""
            disabled={disabled}
          >
            <i class="fa-solid fa-up-right-and-down-left-from-center"></i>
          </Button>
        </OverlayTrigger>

        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.generatively_extend_image || "Generatively Extend Image")}>
          <Button
            variant="light"
            size="large"
            style={{ zIndex: 101 }}
            onClick={() => handleGenerativeExtension()}
            className=""
            disabled={disabled || isExtensionInProgress || !isGenerativeExtensionPossible}
            help="test"
          >
            {isExtensionInProgress ? (
              <LoadingSpinner size={16} />
            ) : (
              <i class="fa-solid fa-brain"></i>
            )}
          </Button>
        </OverlayTrigger>

        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.rotate_left || "Rotate Left")}>
          <Button
            variant="light"
            size="large"
            style={{ zIndex: 101 }}
            onClick={() => rotateCropperImage(-90)}
            className=""
            disabled={disabled}
          >
            <i className="fad fa-undo-alt" />
          </Button>
        </OverlayTrigger>

        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.rotate_right || "Rotate Right")}>
          <Button
            variant="light"
            size="large"
            style={{ zIndex: 101 }}
            onClick={() => rotateCropperImage(90)}
            className=""
            disabled={disabled}
          >
            <i className="fad fa-redo-alt" />
          </Button>
        </OverlayTrigger>

        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.flip_horizontal || "Flip Horizontal")}>
          <Button
            variant="light"
            size="large"
            style={{ zIndex: 101 }}
            onClick={() =>
              mirrorCropperImage(
                cropperRef.current?.cropper.getData().scaleX * -1,
                cropperRef.current?.cropper.getData().scaleY
              )
            }
            className=""
            disabled={disabled}
          >
            <i className="fad fa-exchange-alt" />
          </Button>
        </OverlayTrigger>

        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.flip_vertical || "Flip Vertical")}>
          <Button
            variant="light"
            size="large"
            style={{ zIndex: 101 }}
            onClick={() =>
              mirrorCropperImage(
                cropperRef.current?.cropper.getData().scaleX,
                cropperRef.current?.cropper.getData().scaleY * -1
              )
            }
            className=""
            disabled={disabled}
          >
            <i className="fad fa-sort-alt" />
          </Button>
        </OverlayTrigger>

        <OverlayTrigger placement='top' overlay={tooltip(messages.editor.save || "Save Image")}>
          <Button variant="success" size="large" style={{ zIndex: 101 }} onClick={() => imageUpload()} className="" disabled={disabled}>
            <i className="fal fa-save" />
            {' ' + messages.editor.save}
          </Button>
        </OverlayTrigger>
      </div>
      <div className='d-flex align-items-center justify-content-center' style={{ padding: '10px' }}>
        <Form.Check
          checked={markAsAiGenerated}
          onChange={(e) => {
            setMarkAsAiGenerated(e.target.checked);
            if (handleAiGeneratedImage) {
              handleAiGeneratedImage(e.target.checked);
            }
          }}
          disabled={disabled}
          label={messages.editor.mark_as_ai_generated || "Mark as AI Generated"}
          style={{ width: 'max-content', margin: '0 auto', padding: '10px' }}
        />
      </div>
      {allowZoom && (
        <Slider
          step={0.01}
          min={0.01}
          max={parseFloat(possibleZooms) - 0.01}
          defaultValue={xZoom}
          value={xZoom}
          onChange={x => zoomCropperImage(x)}
        />
      )}

      {freeScale ? (
        <>
          {!scaleFree ? (
            <Button
              size="large"
              onClick={() => {
                setCropperScaleFree(NaN);
              }}
              className="scaleFree"
              disabled={disabled}
            >
              {messages.editor.set_scale_free}
            </Button>
          ) : (
            <Button
              size="large"
              onClick={() => {
                setCropperScaleFree(1 / ratio);
              }}
              className="scaleFree"
              disabled={disabled}
            >
              {messages.editor.unset_scale_free}
            </Button>
          )}
        </>
      ) : (
        <></>
      )}
    </>
  );
}
export default ImageEditor;
